import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe, Instagram, ChevronDown } from "lucide-react";
import { useLang, LANGUAGES } from "../lib/LanguageContext";
import { useCurrency, CURRENCIES, CURRENCY_SYMBOLS } from "../lib/CurrencyContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import SearchBar from "./SearchBar";

const NAV_LINKS = [
    { to: "/", key: "nav_home" },
    { to: "/rooms", key: "nav_rooms" },
    { to: "/facilities", key: "nav_facilities" },
    { to: "/gallery", key: "nav_gallery" },
    { to: "/blog", key: "nav_blog" },
    { to: "/faq", key: "nav_faq" },
    { to: "/contact", key: "nav_contact" },
];

export default function Header() {
    const { lang, setLang, t } = useLang();
    const { currency, setCurrency, setRates } = useCurrency();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [curOpen, setCurOpen] = useState(false);
    const [site, setSite] = useState(null);
    const location = useLocation();
    const isHome = location.pathname === "/";
    const langRef = useRef(null);
    const curRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        api.get("/site/content").then((r) => {
            setSite(r.data);
            if (r.data?.currency_rates && typeof r.data.currency_rates === "object") {
                setRates(r.data.currency_rates);
            }
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setOpen(false);
        setLangOpen(false);
        setCurOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!langOpen && !curOpen) return;
        const onClick = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) {
                setLangOpen(false);
            }
            if (curRef.current && !curRef.current.contains(e.target)) {
                setCurOpen(false);
            }
        };
        const id = setTimeout(() => document.addEventListener("click", onClick), 0);
        return () => {
            clearTimeout(id);
            document.removeEventListener("click", onClick);
        };
    }, [langOpen, curOpen]);

    const useDarkText = scrolled || !isHome;

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled || !isHome
                    ? "glass shadow-[0_4px_30px_rgb(0,0,0,0.04)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="header-logo"
                    className={`font-serif text-xl md:text-2xl tracking-tight ${
                        useDarkText ? "text-[#0F3A59]" : "text-white"
                    }`}
                >
                    Kompleksi Dea
                    <span className="block text-[10px] md:text-xs font-sans font-light tracking-[0.3em] uppercase mt-0.5 opacity-70">
                        Radhimë
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.key}`}
                            className={({ isActive }) =>
                                `text-sm tracking-wide font-light transition-colors hover:opacity-100 ${
                                    isActive
                                        ? useDarkText
                                            ? "text-[#0F3A59] font-medium"
                                            : "text-white font-medium"
                                        : useDarkText
                                          ? "text-[#1C1C1C]/80 hover:text-[#0F3A59]"
                                          : "text-white/85 hover:text-white"
                                }`
                            }
                        >
                            {t(l.key)}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    {site?.instagram && (
                        <a
                            href={site.instagram}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="header-instagram"
                            aria-label="Instagram"
                            className={`hidden sm:flex items-center justify-center w-9 h-9 rounded-full border transition ${
                                useDarkText
                                    ? "border-[#0F3A59]/20 text-[#0F3A59] hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white hover:border-transparent"
                                    : "border-white/40 text-white hover:bg-white/20"
                            }`}
                        >
                            <Instagram size={16} strokeWidth={1.5} />
                        </a>
                    )}
                    <SearchBar useDarkText={useDarkText} iconOnly={true} buttonClass="hidden sm:flex w-9 h-9 items-center justify-center rounded-full border border-current/0 hover:bg-current/5" />
                    <div className="hidden sm:block relative" ref={curRef}>
                        <button
                            data-testid="currency-toggle"
                            onClick={() => setCurOpen(!curOpen)}
                            className={`flex items-center gap-1 text-xs font-medium tracking-wider uppercase transition-colors px-3 py-1.5 rounded-full border ${
                                useDarkText
                                    ? "border-[#0F3A59]/20 text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                                    : "border-white/40 text-white hover:bg-white hover:text-[#0F3A59]"
                            }`}
                        >
                            <span className="text-sm">{currency === "ALL" ? "L" : CURRENCY_SYMBOLS[currency]}</span>
                            {currency}
                            <ChevronDown size={12} className={`transition-transform ${curOpen ? "rotate-180" : ""}`} />
                        </button>
                        {curOpen && (
                            <div
                                data-testid="currency-dropdown"
                                className="absolute right-0 top-12 bg-white rounded-2xl shadow-2xl border border-[#E5E0D8] py-2 min-w-[140px] z-50"
                            >
                                {CURRENCIES.map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => { setCurrency(c); setCurOpen(false); }}
                                        data-testid={`currency-option-${c}`}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-[#F4EFE6] transition-colors ${
                                            currency === c ? "text-[#0F3A59] font-semibold bg-[#F4EFE6]/50" : "text-[#1C1C1C]"
                                        }`}
                                    >
                                        <span className="font-serif w-5 text-center">{CURRENCY_SYMBOLS[c]}</span>
                                        <span>{c}</span>
                                        {currency === c && <span className="ml-auto text-[#0F3A59]">✓</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        data-testid="lang-toggle"
                        onClick={() => setLangOpen(!langOpen)}
                        className={`hidden sm:flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase transition-colors px-3 py-1.5 rounded-full border ${
                            useDarkText
                                ? "border-[#0F3A59]/20 text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                                : "border-white/40 text-white hover:bg-white hover:text-[#0F3A59]"
                        }`}
                    >
                        <Globe size={14} strokeWidth={1.5} />
                        {LANGUAGES.find((l) => l.code === lang)?.label || "AL"}
                        <ChevronDown size={12} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
                    </button>
                    {langOpen && (
                        <div
                            ref={langRef}
                            data-testid="lang-dropdown"
                            className="absolute right-4 md:right-12 top-16 bg-white rounded-2xl shadow-2xl border border-[#E5E0D8] py-2 min-w-[160px] z-50"
                        >
                            {LANGUAGES.map((l) => (
                                <button
                                    key={l.code}
                                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                                    data-testid={`lang-option-${l.code}`}
                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-[#F4EFE6] transition-colors ${
                                        lang === l.code ? "text-[#0F3A59] font-semibold bg-[#F4EFE6]/50" : "text-[#1C1C1C]"
                                    }`}
                                >
                                    <span className="text-base">{l.flag}</span>
                                    <span>{l.name}</span>
                                    {lang === l.code && (
                                        <span className="ml-auto text-[#0F3A59]">✓</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    <Link to="/book" className="hidden md:block">
                        <Button
                            data-testid="header-book-button"
                            className="bg-[#0F3A59] hover:bg-[#1C5279] text-white px-6 py-5 rounded-full font-medium text-sm tracking-wide"
                        >
                            {t("book_now")}
                        </Button>
                    </Link>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className={`lg:hidden p-2 ${useDarkText ? "text-[#0F3A59]" : "text-white"}`}
                        aria-label="menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-white border-t border-[#E5E0D8] shadow-lg"
                >
                    <div className="px-6 py-6 flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-${l.key}`}
                                className={({ isActive }) =>
                                    `py-3 text-base font-light border-b border-[#E5E0D8] last:border-0 ${
                                        isActive
                                            ? "text-[#0F3A59] font-medium"
                                            : "text-[#1C1C1C]"
                                    }`
                                }
                            >
                                {t(l.key)}
                            </NavLink>
                        ))}
                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[#0F3A59]/20 rounded-full">
                                <Globe size={14} className="text-[#0F3A59]" strokeWidth={1.5} />
                                {LANGUAGES.map((l) => (
                                    <button
                                        key={l.code}
                                        onClick={() => setLang(l.code)}
                                        data-testid={`mobile-lang-${l.code}`}
                                        className={`text-xs font-medium uppercase tracking-wider px-2 py-0.5 rounded-full transition ${
                                            lang === l.code
                                                ? "bg-[#0F3A59] text-white"
                                                : "text-[#0F3A59] hover:bg-[#F4EFE6]"
                                        }`}
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                            {site?.instagram && (
                                <a
                                    href={site.instagram}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="mobile-instagram"
                                    className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full text-white bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]"
                                >
                                    <Instagram size={16} strokeWidth={1.5} />
                                    Instagram
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
